import { Localized } from '../schema/localized'
import { MenuCategory, MenuDish, MenuSnapshot } from '../render/menu'

/** What the page needs of a dish, out of everything the RMS holds on one. */
export interface DishRecord {
  id?: string
  price: number
  menuNumber: number
  translations: { languageCode: string; name?: string; description?: string }[]
  dishCategoryIds?: string[]
  image?: { url: string }
}

export interface CategoryRecord {
  id?: string
  translations: { languageCode: string; name?: string }[]
}

/** The group the dishes that sit in no listed category are shown under. */
export const UNCATEGORIZED_ID = 'uncategorized'

/** A list of translations as the page reads them: a value per language. */
const localized = (
  translations: { languageCode: string; name?: string; description?: string }[],
  field: 'name' | 'description'
): Localized<string> =>
  Object.fromEntries(
    translations
      .filter(item => item[field])
      .map(item => [item.languageCode, item[field] as string])
  )

const toDish = (dish: DishRecord & { id: string }): MenuDish => ({
  id: dish.id,
  name: localized(dish.translations, 'name'),
  description: localized(dish.translations, 'description'),
  price: dish.price,
  ...(dish.image?.url ? { imageUrl: dish.image.url } : {})
})

/**
 * The menu as the page draws it, from the two lists the RMS keeps.
 *
 * Every dish is shown once per category it sits in, in the venue's own order,
 * and a dish that sits in no listed category is not dropped: it goes into a
 * last group of its own, because a dish somebody priced and photographed and
 * never filed belongs on the menu more than a filing rule does. The page
 * names that group itself, in its own language.
 */
export function menuSnapshotFrom(
  dishes: DishRecord[],
  categories: CategoryRecord[],
  currency: string
): MenuSnapshot {
  const known = dishes.filter(
    (dish): dish is DishRecord & { id: string } => Boolean(dish.id)
  )
  const byMenuNumber = (a: DishRecord, b: DishRecord) =>
    a.menuNumber - b.menuNumber

  const listed: MenuCategory[] = categories
    // The models type the ids as optional; a row without one is not a row.
    .filter((category): category is CategoryRecord & { id: string } =>
      Boolean(category.id)
    )
    .map(category => ({
      id: category.id,
      name: localized(category.translations, 'name'),
      dishes: known
        .filter(dish => (dish.dishCategoryIds ?? []).includes(category.id))
        .sort(byMenuNumber)
        .map(toDish)
    }))
    // A category with nothing in it is not a heading anybody needs.
    .filter(category => category.dishes.length > 0)

  const listedIds = new Set(listed.map(category => category.id))
  const unfiled = known
    .filter(
      dish => !(dish.dishCategoryIds ?? []).some(id => listedIds.has(id))
    )
    .sort(byMenuNumber)
    .map(toDish)

  return {
    currency,
    categories:
      unfiled.length > 0
        ? [...listed, { id: UNCATEGORIZED_ID, name: {}, dishes: unfiled }]
        : listed
  }
}
